/**
 * TowerHammer - Rotating hammer tower
 *
 * Uses a rotating weapon attachment that damages enemies on contact.
 */
import { Vector } from '../../core/math/vector';
import { Line } from '../../core/math/line';
import { Circle } from '../../core/math/circle';
import { MyColor } from '../../entities/myColor';
import { CircleObject } from '../../entities/base/circleObject';
import { Tower } from './tower';
import { TowerRegistry } from '../towerRegistry';
import { renderTowerHammer } from '../rendering/towerRenderer';
import { scalePeriod } from '../../core/speedScale';
import { isEnemy } from '@/game/player/ownership';
import type {
    VectorLike,
    CircleLike,
    MonsterLike as BaseMonsterLike,
    TerritoryLike,
    FogOfWarLike,
    UserLike,
    TowerLike,
} from '@/types/worldLike';

// Extended VectorLike with dis method
interface VectorLikeExt extends VectorLike {
    dis(other: VectorLike): number;
    disSq(other: VectorLike): number;
}

// Extended CircleLike with impact method
interface CircleLikeExt extends CircleLike {
    impact(other: CircleLike): boolean;
}

// Extended MonsterLike for hammer tower
interface MonsterLike extends BaseMonsterLike {
    pos: VectorLikeExt;
    getBodyCircle(): CircleLikeExt;
}

// WorldLike interface for TowerHammer
interface WorldLike {
    width: number;
    height: number;
    batterys: TowerLike[];
    territory?: TerritoryLike;
    fog?: FogOfWarLike;
    user: UserLike;
    getMonstersInRange(x: number, y: number, range: number): MonsterLike[];
    addBullet(bullet: unknown): void;
    removeBullet(bullet: unknown): void;
}

// 碰撞冷却帧数（约0.5秒 @ 60fps，考虑速度缩放）
const HIT_COOLDOWN_FRAMES = 30;

export class TowerHammer extends Tower {
    itemRange: number;
    itemRidus: number;
    itemDamage: number;
    itemSpeed: number;
    additionItem: CircleObject;
    // 记录每个怪物的最后受击帧，用于冷却判定
    private hitCooldown: WeakMap<object, number> = new WeakMap();

    constructor(x: number, y: number, world: any) {
        super(x, y, world);
        this.name = "附属物品塔楼";
        this.clock = 1.1;

        this.itemRange = this.rangeR;
        this.itemRidus = 20;
        this.itemDamage = 1000;
        this.itemSpeed = scalePeriod(10);
        this.additionItem = this.initAdditionItem();
    }

    initAdditionItem(): CircleObject {
        let loc = this.pos.plus(Vector.randCircle().mul(this.itemRange));
        let c = new CircleObject(loc, this.world as unknown as ConstructorParameters<typeof CircleObject>[1]);
        c.r = this.itemRidus;

        c.hpInit(-1);

        c.bodyColor = new MyColor(60, 63, 65, 1);
        c.bodyStrokeColor = new MyColor(31, 31, 31, 0);
        return c;
    }

    toTarget(): void {
        const target = this.findFirstTarget();
        if (target) {
            this.itemRange = Math.sqrt(target.pos.disSq(this.pos));
        }
    }

    itemRender(ctx: CanvasRenderingContext2D): void {
        this.additionItem.render(ctx);
        let line = new Line(this.pos, this.additionItem.pos);
        line.strokeWidth = 10;
        line.strokeColor = this.additionItem.bodyColor;
        line.render(ctx);
    }

    render(ctx: CanvasRenderingContext2D): void {
        renderTowerHammer(this, ctx);
    }

    /**
     * 渲染塔主体（不含状态条）
     */
    renderBody(ctx: CanvasRenderingContext2D): void {
        super.renderBody(ctx);
        this.itemRender(ctx);
    }

    goStep(): void {
        // 保持向后兼容：执行完整的更新逻辑
        this.goStepMove();
        this.goStepCollide();
    }

    /**
     * 移动阶段：处理锤子物品的位置更新
     */
    goStepMove(): void {
        super.goStepMove();
        // 更新锤子物品位置
        let a = this.itemSpeed;
        let loc = new Vector(Math.sin(this.liveTime / a), Math.cos(this.liveTime / a)).mul(this.itemRange);
        this.additionItem.pos = this.pos.plus(loc);
    }

    /**
     * 碰撞阶段：处理锤子的目标检测和碰撞
     */
    goStepCollide(): void {
        super.goStepCollide();
        this.toTarget();
        // 锤子物品碰撞检测
        let itemPos = this.additionItem.pos;
        let itemR = this.additionItem.r;
        let nearbyMonsters = this.world.getMonstersInRange(itemPos.x, itemPos.y, itemR + 50);
        let itemCircle = this.additionItem.getBodyCircle();
        let actualDamage = this.itemDamage * this.getDamageMultiplier();
        for (let m of nearbyMonsters) {
            // Filter friendly monsters (same owner)
            if (!isEnemy(this, m)) {
                continue;
            }
            const mc = m.getBodyCircle();
            if (this.world.fog?.enabled && !this.world.fog.isCircleVisible(mc.x, mc.y, mc.r)) {
                continue;
            }
            // 检查冷却，避免同一怪物每帧都受伤害
            const lastHit = this.hitCooldown.get(m) ?? -HIT_COOLDOWN_FRAMES;
            if (this.liveTime - lastHit < HIT_COOLDOWN_FRAMES) {
                continue;
            }
            if (Circle.collides(itemCircle.x, itemCircle.y, itemCircle.r, mc.x, mc.y, mc.r)) {
                m.hpChange(-actualDamage, this.ownerId);
                this.hitCooldown.set(m, this.liveTime);
            }
        }
    }
}

// Register class type for save system
TowerRegistry.registerClassType('TowerHammer', () => TowerHammer);
